import { Image, View, Text, FlatList, StyleSheet, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Colors } from '@/constants/colors';
import { useFavoritesStore } from '@/store/favorites';
import { EmptyState } from '@/components/EmptyState';
import { Sport } from '@/types';

const SPORT_COLOR: Record<Sport, string> = {
  futebol: Colors.futebol,
  nba: Colors.nba,
  nfl: Colors.nfl,
};

const SPORT_LABEL: Record<Sport, string> = {
  futebol: '⚽ Futebol',
  nba: '🏀 NBA',
  nfl: '🏈 NFL',
};

export default function FavoritosScreen() {
  const { favorites, removeFavorite } = useFavoritesStore();

  const grouped = favorites.reduce<Record<Sport, typeof favorites>>((acc, team) => {
    if (!acc[team.sport]) acc[team.sport] = [];
    acc[team.sport].push(team);
    return acc;
  }, {} as any);

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <View style={styles.header}>
        <Text style={styles.title}>⭐ Favoritos</Text>
      </View>

      <FlatList
        data={[]}
        renderItem={null}
        ListHeaderComponent={
          <>
            {/* Times favoritos */}
            {favorites.length === 0 ? (
              <EmptyState
                icon="⭐"
                title="Nenhum time favorito"
                subtitle="Toque no ícone ⭐ em qualquer jogo para adicionar um time e receber notificações"
              />
            ) : (
              Object.entries(grouped).map(([sport, teams]) => (
                <View key={sport}>
                  <Text style={[styles.sportSection, { color: SPORT_COLOR[sport as Sport] }]}>
                    {SPORT_LABEL[sport as Sport]}
                  </Text>
                  {teams.map((team) => (
                    <View key={`${team.sport}:${team.id}`} style={styles.teamRow}>
                      <Image source={{ uri: team.logo }} style={styles.teamLogo} resizeMode="contain" />
                      <Text style={styles.teamName}>{team.name}</Text>
                      <TouchableOpacity
                        style={styles.removeBtn}
                        onPress={() => removeFavorite(team.id, team.sport)}
                      >
                        <Text style={styles.removeBtnText}>Remover</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              ))
            )}

            {/* Dica: notificações ficam na aba Alertas */}
            <View style={styles.notifHint}>
              <Text style={styles.notifHintText}>
                🔔 As configurações de notificação agora ficam na aba <Text style={styles.notifHintBold}>Alertas</Text>
              </Text>
            </View>
          </>
        }
        contentContainerStyle={styles.list}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Colors.background },
  header: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  title: { color: Colors.text, fontSize: 22, fontWeight: '800' },
  sportSection: {
    fontSize: 13,
    fontWeight: '700',
    paddingHorizontal: 16,
    paddingTop: 20,
    paddingBottom: 8,
    letterSpacing: 0.5,
  },
  teamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
    gap: 12,
  },
  teamLogo: { width: 32, height: 32 },
  teamName: { color: Colors.text, fontSize: 14, fontWeight: '600', flex: 1 },
  removeBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: 'rgba(239,68,68,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(239,68,68,0.3)',
  },
  removeBtnText: { color: '#f87171', fontSize: 11, fontWeight: '600' },
  notifHint: {
    marginHorizontal: 16,
    marginTop: 28,
    padding: 14,
    backgroundColor: Colors.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  notifHintText: { color: Colors.textSecondary, fontSize: 13, lineHeight: 18 },
  notifHintBold: { color: Colors.futebol, fontWeight: '700' },
  list: { paddingBottom: 32 },
});
